import { useCallback, useEffect, useState } from 'react'

export interface LetterboxdStatus {
  connected: boolean
  username: string | null
  syncedAt: string | null
  ratingCount: number
}

export interface LbMessage {
  type: 'success' | 'error'
  text: string
}

export function useLetterboxdIntegration() {
  const [status, setStatus] = useState<LetterboxdStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<LbMessage | null>(null)
  const [username, setUsername] = useState('')

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetch('/api/letterboxd/status', { credentials: 'include' })
      if (r.ok) {
        const d = (await r.json()) as LetterboxdStatus
        setStatus(d)
        setUsername(d.username ?? '')
      }
    } catch {
      // degrade gracefully
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchStatus()
  }, [fetchStatus])

  const saveUsername = useCallback(async () => {
    setBusy(true)
    setMessage(null)
    try {
      const r = await fetch('/api/letterboxd/username', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim() || null }),
      })
      const d = (await r.json().catch(() => ({}))) as Partial<LetterboxdStatus> & { error?: string }
      if (r.ok) {
        setStatus(d as LetterboxdStatus)
        setMessage({ type: 'success', text: 'Letterboxd username saved' })
      } else {
        setMessage({ type: 'error', text: d.error || 'Failed to save username' })
      }
    } catch {
      setMessage({ type: 'error', text: 'Could not reach server' })
    } finally {
      setBusy(false)
    }
  }, [username])

  const syncRatings = useCallback(async () => {
    setBusy(true)
    setMessage(null)
    try {
      const r = await fetch('/api/letterboxd/sync', { method: 'POST', credentials: 'include' })
      const d = (await r.json().catch(() => ({}))) as { message?: string; error?: string }
      if (r.ok) {
        setMessage({ type: 'success', text: d.message || 'Synced from Letterboxd' })
        await fetchStatus()
      } else {
        setMessage({ type: 'error', text: d.error || 'Sync failed' })
      }
    } catch {
      setMessage({ type: 'error', text: 'Could not reach server' })
    } finally {
      setBusy(false)
    }
  }, [fetchStatus])

  const importZip = useCallback(
    async (file: File) => {
      setBusy(true)
      setMessage(null)
      try {
        const fd = new FormData()
        fd.append('file', file)
        const r = await fetch('/api/letterboxd/import', {
          method: 'POST',
          credentials: 'include',
          body: fd,
        })
        const d = (await r.json().catch(() => ({}))) as { message?: string; error?: string }
        if (r.ok) {
          setMessage({ type: 'success', text: d.message || 'Import complete' })
          await fetchStatus()
        } else {
          setMessage({ type: 'error', text: d.error || 'Import failed' })
        }
      } catch {
        setMessage({ type: 'error', text: 'Could not reach server' })
      } finally {
        setBusy(false)
      }
    },
    [fetchStatus]
  )

  const disconnect = useCallback(async () => {
    setBusy(true)
    try {
      const r = await fetch('/api/letterboxd/disconnect', { method: 'POST', credentials: 'include' })
      if (r.ok) {
        await fetchStatus()
        setMessage({ type: 'success', text: 'Letterboxd disconnected' })
      }
    } catch {
      // ignore
    } finally {
      setBusy(false)
    }
  }, [fetchStatus])

  return {
    status,
    loading,
    busy,
    message,
    setMessage,
    username,
    setUsername,
    saveUsername,
    syncRatings,
    importZip,
    disconnect,
  }
}
